import React from "react";
import {
  UserRound,
  Users, GraduationCap, ShoppingBag, CalendarDays, Gift,
  ShieldCheck, ShieldAlert, ScrollText, Settings, ChevronLeft, ChevronRight, Sparkles, BarChart3, MailCheck, Award, PenLine, FileSignature,
  BookOpen, Ticket, LifeBuoy,
} from "lucide-react";

/**
 * Left navigation. Groups tabs into CRM / Management / System sections
 * to reduce visual competition with the operational toolbar.
 *
 * Collapses to a 64px icon-only rail on desktop and slides off-screen
 * on mobile (handled by parent via `mobileOpen`/`onMobileClose`).
 */
const SECTIONS = [
  {
    label: "CRM",
    items: [
      { key: "leads",       label: "Leaduri",   icon: Users },
      { key: "enrollments", label: "Înscrieri", icon: GraduationCap },
      // Owner and admin only: the signed documents carry the participant's CNP.
      { key: "contracts",   label: "Contracte", icon: FileSignature, requires: "readOnlyTabs" },
      { key: "purchases",   label: "Plăți",     icon: ShoppingBag, requires: "notPartner" },
      { key: "consents",    label: "Consimțăminte", icon: MailCheck, requires: "readOnlyTabs" },
      { key: "analytics",   label: "Analytics", icon: BarChart3,   requires: "notPartner" },
    ],
  },
  {
    label: "Management",
    items: [
      { key: "groups",    label: "Grupe curs",  icon: CalendarDays },
      // Owner and admin FPED: the trainers' data is served to them only (19.10.2026: a Viewer saw an error).
      { key: "trainers",  label: "Traineri",    icon: UserRound, requires: "readOnlyTabs" },
      // Owner and admin only: participants' work is personal data.
      { key: "portal",    label: "Portal",      icon: BookOpen, requires: "readOnlyTabs" },
      { key: "webinars",  label: "Webinarii",   icon: Sparkles, requires: "notPartner" },
      { key: "certificates", label: "Adeverințe", icon: Award, requires: "notPartner" },
      { key: "referrals", label: "Recomandări", icon: Gift, badge: "referralBadge", requires: "notPartner" },
    ],
  },
  {
    label: "Sistem",
    items: [
      // Everybody in the admin: each reads their own; owner and admin FPED all (14.10.2026).
      { key: "tickets",  label: "Tichete",     icon: Ticket, badge: "ticketBadge" },
      // Owner and admin FPED: how the platform works, kept with the code (19.10.2026).
      { key: "ghid",     label: "Ghid",        icon: LifeBuoy, requires: "readOnlyTabs", badge: "guideBadge" },
      { key: "signature", label: "Semnătură email", icon: PenLine, requires: "readOnlyTabs" },
      { key: "users",    label: "Utilizatori", icon: ShieldCheck, requires: "readOnlyTabs" },
      { key: "security", label: "Securitate",  icon: ShieldAlert, requires: "owner" },
      { key: "settings", label: "Setări curs", icon: Settings,    requires: "readOnlyTabs" },
      { key: "audit",    label: "Audit",       icon: ScrollText,  requires: "owner" },
    ],
  },
];

/**
 * Every tab key this sidebar can navigate to.
 *
 * Exported so a test can compare it with the dashboard's slug list. Adding a
 * tab means touching two files, and the day I added "Traineri" I touched one:
 * the link rendered, was judged an unknown slug, and silently bounced to
 * Leaduri. Nothing was broken enough to look broken.
 */
export const SIDEBAR_TAB_KEYS = SECTIONS.flatMap((s) => s.items.map((i) => i.key));


export default function AdminSidebar({
  activeTab,
  onSelect,
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onMobileClose,
  isOwner,
  readOnlyTabsVisible,
  isPartner = false,
  badges = {},
  // brandLogo prop deprecated since the topbar refactor (logo now lives in
  // AdminDashboard topbar). Kept in signature for backwards-compat with any
  // future callers; intentionally unused inside this component.
  brandLogo: _brandLogo,
}) {
  const canShow = (req) => {
    if (!req) return true;
    if (req === "owner") return isOwner;
    if (req === "readOnlyTabs") return readOnlyTabsVisible;
    if (req === "notPartner") return !isPartner;
    return true;
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        data-testid="admin-sidebar"
        className={`fixed top-14 bottom-0 left-0 z-40 bg-white border-r border-[#E5E7EB] flex flex-col transition-[width,transform] duration-200 ease-out
          ${collapsed ? "w-16" : "w-60"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0`}
      >
        {/* Collapse toggle — sidebar-only chrome. Logo + title live in the
            full-width topbar so the brand never gets squashed. */}
        <div className={`h-10 flex items-center border-b border-[#E5E7EB] ${collapsed ? "justify-center px-1" : "justify-end px-3"}`}>
          <button
            onClick={onToggleCollapsed}
            className="hidden lg:inline-flex text-[#6B7280] hover:text-[#373A45] rounded-md p-1 hover:bg-[#F5F6F6]"
            aria-label={collapsed ? "Extinde sidebar" : "Restrânge sidebar"}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4" data-testid="admin-sidebar-nav">
          {SECTIONS.map((section) => {
            const visibleItems = section.items.filter((it) => canShow(it.requires));
            if (!visibleItems.length) return null;
            return (
              <div key={section.label}>
                {!collapsed && (
                  <div className="px-2 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">
                    {section.label}
                  </div>
                )}
                <ul className="space-y-0.5">
                  {visibleItems.map((it) => {
                    const Icon = it.icon;
                    const active = activeTab === it.key;
                    const badge = it.badge ? badges[it.badge] : null;
                    return (
                      <li key={it.key}>
                        <button
                          onClick={() => { onSelect(it.key); onMobileClose?.(); }}
                          data-testid={`sidebar-tab-${it.key}`}
                          title={collapsed ? it.label : undefined}
                          className={`w-full flex items-center gap-2.5 rounded-lg text-sm font-medium transition-colors
                            ${collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2"}
                            ${active
                              ? "bg-[#373A45] text-white"
                              : "text-[#373A45] hover:bg-[#F5F6F6]"}`}
                        >
                          <Icon size={16} className="shrink-0" />
                          {!collapsed && <span className="flex-1 text-left truncate">{it.label}</span>}
                          {Number(badge) > 0 && (
                            <span
                              className={`rounded-full text-[10px] font-bold leading-none ${collapsed ? "absolute mt-[-18px] ml-7 px-1 py-0.5" : "px-1.5 py-0.5"} ${active ? "bg-white text-[#373A45]" : "bg-[#519872] text-white"}`}
                              data-testid={`sidebar-badge-${it.key}`}
                            >
                              {badge}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
